import { useState, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import type { Room } from '../types';

const API_BASE = 'https://server.gdyx.us.ci';

export function HallPage() {
  const { userId, userName, token, logout, setPage, rooms, setRooms, setCurrentRoom, setMyPosition } = useGameStore();
  const [showCreate, setShowCreate] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/rooms`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRooms(data.rooms || []);
      }
    } catch (e) {
      console.error('获取房间列表失败', e);
    }
  };

  const createRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ room_name: roomName }),
      });
      const data = await res.json();
      if (res.ok) {
        setShowCreate(false);
        joinRoom(data.room_id);
      }
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = async (roomId: number) => {
    try {
      const wsUrl = `${API_BASE.replace('http', 'ws')}/ws/${roomId}`;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        ws.send(JSON.stringify({
          type: 'room_join',
          data: {
            roomId,
            playerId: userId,
            playerName: userName,
          },
        }));
      };

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'room_joined': {
            useGameStore.getState().setWs(ws);
            const room = msg.data.room as Room;
            setCurrentRoom(room);
            setMyPosition(msg.data.position);
            setPage('game');
            break;
          }
          case 'error':
            alert(msg.data.message);
            ws.close();
            break;
        }
      };

      ws.onclose = () => {
        useGameStore.getState().setWs(null);
      };
    } catch (e) {
      console.error('加入房间失败', e);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-800 to-green-600 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-green-800">游戏大厅</h1>
            <div className="flex items-center gap-4">
              <span className="text-gray-600">欢迎, {userName}</span>
              <button
                onClick={logout}
                className="text-gray-500 hover:text-gray-700"
              >
                退出
              </button>
            </div>
          </div>

          <button
            onClick={() => setShowCreate(true)}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition"
          >
            创建房间
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-lg font-semibold mb-4">房间列表</h2>
          
          {rooms.length === 0 ? (
            <p className="text-gray-500 text-center py-8">暂无房间，请创建新房间</p>
          ) : (
            <div className="space-y-2">
              {rooms.map((room) => (
                <div
                  key={room.id}
                  className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div>
                    <span className="font-medium">{room.name}</span>
                    <span className="text-gray-500 text-sm ml-2">
                      {room.playerCount}/4 人
                    </span>
                  </div>
                  <button
                    onClick={() => joinRoom(room.id)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                  >
                    加入
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-xl font-semibold mb-4">创建房间</h3>
            <form onSubmit={createRoom}>
              <input
                type="text"
                placeholder="房间名称"
                value={roomName}
                onChange={e => setRoomName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
                required
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 bg-gray-200 py-2 rounded-lg"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg disabled:opacity-50"
                >
                  创建
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}